import { useEffect, useRef } from 'react';

/**
 * Preserve the scroll position of the supplied element (or window if none is
 * supplied) when the tab is backgrounded and restore it when the tab becomes
 * visible again. The position is also written to sessionStorage so it can be
 * restored after a dev-server hard reload.
 */
export function useScrollRestoration(elementRef?: React.RefObject<HTMLElement>) {
  const scrollPos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Key the storage by pathname so different pages remember their own position
  const storageKey = `scroll-pos:${typeof window !== 'undefined' ? location.pathname : ''}`;

  
  useEffect(() => {
    const getTarget = (): HTMLElement | (Window & typeof globalThis) => {
      return elementRef?.current ?? window;
    };

    const save = () => {
      const target = getTarget();
      const x = target instanceof Window ? target.scrollX : (target as HTMLElement).scrollLeft;
      const y = target instanceof Window ? target.scrollY : (target as HTMLElement).scrollTop;
      scrollPos.current = { x, y };
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(scrollPos.current));
      } catch {
        /* ignore */
      }
    };

    const restore = () => {
      // Use rAF so we run after the browser has done any layout work on focus
      requestAnimationFrame(() => {
        const target = getTarget();
        const { x, y } = scrollPos.current;
        if (target instanceof Window) {
          target.scrollTo(x, y);
        } else {
          (target as HTMLElement).scrollTo({ left: x, top: y });
        }
      });
    };

    // Initialise from sessionStorage (covers full page reload case)
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        scrollPos.current = JSON.parse(stored);
        restore();
      }
    } catch {
      /* ignore */
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        save();
      } else if (document.visibilityState === 'visible') {
        restore();
      }
    };

    const target = getTarget();

    // Attach listeners
    window.addEventListener('blur', save);
    window.addEventListener('focus', restore);
    document.addEventListener('visibilitychange', handleVisibility);

    if (!(target instanceof Window)) {
      target.addEventListener('scroll', save, { passive: true });
    }

    return () => {
      window.removeEventListener('blur', save);
      window.removeEventListener('focus', restore);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (!(target instanceof Window)) {
        target.removeEventListener('scroll', save);
      }
    };
  }, [elementRef, storageKey]);
} 